import { Icon } from "@/components/icons/Icon";
import { displayHotkey, isKnownHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";

import type { DropdownActionItem } from "./Dropdown";

/** Base layout for an action-item button; surfaces add their own focus/active styling. */
/**
 * Rings the current item of a menu opened from the keyboard. A Headless UI menu keeps focus on its
 * panel and marks the current item `data-focus`, so the item never matches `:focus-visible` itself;
 * a menu opened with the mouse has no `:focus-visible` panel and keeps the plain highlight.
 */
export const MENU_ITEM_FOCUS_RING_CLASS =
  "[:focus-visible>&]:data-[focus]:outline-solid [:focus-visible>&]:data-[focus]:outline-2 [:focus-visible>&]:data-[focus]:-outline-offset-2 [:focus-visible>&]:data-[focus]:outline-blue-500";

export const MENU_ITEM_BUTTON_CLASS = twMerge(
  "w-full min-h-8.5 py-1.25 px-2.5 text-sm flex items-center rounded-8 whitespace-nowrap tracking-tight",
  "sm:text-[16px]",
  MENU_ITEM_FOCUS_RING_CLASS,
);

export function MenuItemContent({ item }: { item: DropdownActionItem }) {
  const hotkey = isKnownHotkey(item.hotkey) ? displayHotkey(item.hotkey) : item.hotkey;

  return (
    <>
      {item.icon && (
        <div className="min-w-6 h-6 mr-2.5 [&>svg]:w-full [&>svg]:h-full">{item.icon}</div>
      )}
      <span className="inline-flex flex-col text-left">
        <span>
          {item.title}
          {item.title2 && <span className="text-input-placeholder">&nbsp;{item.title2}</span>}
          {item.superscript && (
            <span className="align-super text-input-placeholder text-xxxs">
              &nbsp;{item.superscript}
            </span>
          )}
          {item.caption && (
            <span className="whitespace-nowrap text-gray-400">&nbsp;·&nbsp;{item.caption}</span>
          )}
        </span>
        {item.caption2 && <span className="text-size-13 text-gray-400">{item.caption2}</span>}
      </span>
      {!item.active && hotkey && (
        <span
          aria-hidden
          className={twMerge(
            "ml-auto pl-4.5 text-[13px] min-w-10 text-xxs text-gray-400",
            "sm:hidden",
          )}
        >
          {hotkey}
        </span>
      )}
      {item.active && <Icon name="Check" className="w-6 h-6 ml-auto -mr-1" />}
    </>
  );
}

export function MenuSeparator() {
  return <div className="h-px my-1.25 mx-2.5 bg-gray-200 dark:bg-gray-800" />;
}

export function MenuSectionTitle({ title }: { title: string }) {
  return (
    <div className="min-h-[24px] py-1.5 px-2.5 text-[12px] leading-[14px] text-document-text font-mono uppercase tracking-tight">
      {title}
    </div>
  );
}
